/**
 * CSV Employee Upload Types
 *
 * Types for bulk importing employees from CSV files.
 * Follows a 3-phase flow: Upload → Preview → Confirm
 *
 * Key differences from Tips CSV Import:
 * - No persisted column mappings (one-time mapping per upload)
 * - Handles both new users and existing users
 * - Supports reactivation of inactive employments
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
import { CreateEmployeeInputSchema } from "./employment.js";
// ===== COLUMN MAPPING =====
/**
 * Standard CSV column names that can be auto-detected
 */
export const CSV_EMPLOYEE_COLUMNS = [
    "email",
    "firstName",
    "lastName",
    "phone",
    "preferredName",
    "employmentType",
    "employmentStartDate",
    "companyEmail",
    "employeeId",
    "payrollId",
    "locationNames", // Comma-separated location names
    "locationIds", // Comma-separated location UUIDs
    "jobRoleNames", // Comma-separated job role names
    "jobRoleIds", // Comma-separated job role UUIDs
];
/**
 * Column mappings: CSV header → standard field name
 */
export const CsvEmployeeColumnMappingsSchema = z.record(z.string(), // CSV header
z.enum(CSV_EMPLOYEE_COLUMNS));
// ===== PREVIEW INPUT/OUTPUT =====
/**
 * Input for CSV preview phase
 */
export const CsvEmployeePreviewInputSchema = z.object({
    fileId: uuidSchema,
    columnMappings: CsvEmployeeColumnMappingsSchema.optional(),
});
/**
 * A valid row that can be imported
 */
export const ValidCsvEmployeeRowSchema = z.object({
    rowNumber: z.number().int().positive(),
    data: CreateEmployeeInputSchema,
    /**
     * Status of existing data:
     * - "NEW": Email doesn't exist, will create User + Employment
     * - "LINK": User exists (different company), will create Employment only
     * - "REACTIVATE": Inactive Employment exists, will reactivate
     */
    existingStatus: z.enum(["NEW", "LINK", "REACTIVATE"]),
});
/**
 * An invalid row that will be skipped
 */
export const InvalidCsvEmployeeRowSchema = z.object({
    rowNumber: z.number().int().positive(),
    errors: z.array(z.string()),
    rawData: z.record(z.string(), z.string()),
});
/**
 * A duplicate row (active employment already exists)
 */
export const DuplicateCsvEmployeeRowSchema = z.object({
    rowNumber: z.number().int().positive(),
    email: z.string(),
    existingEmploymentId: z.string(),
});
/**
 * Preview response with validation results
 */
export const CsvEmployeePreviewResponseSchema = z.object({
    // Valid rows by category
    validRows: z.array(ValidCsvEmployeeRowSchema),
    // Problematic rows
    invalidRows: z.array(InvalidCsvEmployeeRowSchema),
    duplicateRows: z.array(DuplicateCsvEmployeeRowSchema),
    // Unmapped references (names that don't match any record)
    unmappedLocations: z.array(z.string()),
    unmappedJobRoles: z.array(z.string()),
    // Column mapping info
    detectedMappings: z
        .object({
        headers: z.array(z.string()),
        suggestedMappings: CsvEmployeeColumnMappingsSchema,
    })
        .nullable(),
    // Summary counts
    summary: z.object({
        totalRows: z.number().int().nonnegative(),
        validRows: z.number().int().nonnegative(),
        newUsers: z.number().int().nonnegative(), // Will create User + Employment
        linkedUsers: z.number().int().nonnegative(), // Will create Employment only
        reactivations: z.number().int().nonnegative(), // Will reactivate Employment
        invalidRows: z.number().int().nonnegative(),
        duplicates: z.number().int().nonnegative(), // Active employments that exist
    }),
});
// ===== CONFIRM INPUT/OUTPUT =====
/**
 * Input for CSV confirm phase
 */
export const CsvEmployeeConfirmInputSchema = z.object({
    fileId: uuidSchema,
    /**
     * Column mappings to use (required if not auto-detected in preview)
     */
    columnMappings: CsvEmployeeColumnMappingsSchema.optional(),
    /**
     * If true, skip rows with active employments (duplicates)
     * If false, fail if any duplicates exist
     * Default: true
     */
    skipDuplicates: z.boolean().optional().default(true),
});
/**
 * Confirm response with import results
 */
export const CsvEmployeeConfirmResponseSchema = z.object({
    // Success counts
    created: z.number().int().nonnegative(), // New Users + Employments
    linked: z.number().int().nonnegative(), // Employments for existing Users
    reactivated: z.number().int().nonnegative(), // Reactivated Employments
    skipped: z.number().int().nonnegative(), // Invalid rows skipped
    duplicatesSkipped: z.number().int().nonnegative(), // Active employment duplicates skipped
    // Errors during import (for rows that passed validation but failed during insert)
    errors: z.array(z.object({
        rowNumber: z.number().int().positive(),
        email: z.string(),
        error: z.string(),
    })),
});
